import {
  customElement,
  FASTElement,
  Observable,
  observable,
  Splice,
} from '@microsoft/fast-element';
import { StructuredChatModalTemplate as template } from './structured-chat.template';
import { StructuredChatModalStyles as styles } from './structured-chat.styles';
import { IProposalInsert, ICompany, IProposal, INegotiation, IVppa, IRfq } from '../../../types';
import { IVppaService } from '../../../services/vppa.service';
import { INegotiationService, IRfqService } from '../../../services';
import { IAppConfigService } from '../../../services/app-config-service';
import { IProjectService } from '../../../services/project.service';
import { logger } from '@genesislcap/foundation-comms';
import {
  IStructuredChatService,
  VALID_STRUCTURED_CHAT_STATUS,
} from '../../../services/structured-chat.service';
import { TextField } from '@genesislcap/foundation-zero';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../services/enum.service';
import { ICompanyService } from '../../../services/company.service';
import { RFQSelector } from './rfq-selector/rfq-selector';
import { AddRFQ as AddRFQForm} from './add-rfq/add-rfq';
import { eventDetail, formatDateForInput, getFormattedNumber } from '../../../utils/formatting';
import { IIndividualProjectPageService } from '../../../services/individual-project-page.service';
import { notifyError } from '../../../utils/notify';
import { PRODUCT_TYPE, PROFILE_RIGHTS } from '../../../utils';
import { getAbbreviationForCreditProfile } from '../../../utils/credit-profiles'
import { IUserService } from '../../../services/user.service';
import {IThirdPartyService} from "../../../services/third-party.service";

const name = 'structured-chat';

export enum Labels {
  TITLE = 'Engagements',
}

export interface StructuredChatEnums {
  applicableRECProgram: Array<EnumTranslate>;
  applicableTrackingSystem: Array<EnumTranslate>;
  recDeliveryMethodology: Array<EnumTranslate>;
  technologyTypes: Array<EnumTranslate>;
  performanceGuaranteeMethod: Array<EnumTranslate>;
  negativePricingMethod: Array<EnumTranslate>;
  negotiationStatus: Array<EnumTranslate>;
  contractTypes: Array<EnumTranslate>;
}

export interface OfferValueChanged {
  fixedPriceHasChanged: boolean;
  offeredCapacityHasChanged: boolean;
  tenorStartDateHasChanged: boolean;
  tenorMonthsHasChanged: boolean;
  buyerCODCollateralHasChanged: boolean;
  sellerCODCollateralHasChanged: boolean;
}

@customElement({
  name,
  template,
  styles,
})
export class StructuredChatModal extends FASTElement {
  // Views
  @observable rfqSelector: RFQSelector;
  @observable addRfqForm: AddRFQForm;

  // Service
  @ICompanyService companyService: ICompanyService;
  @IVppaService vppaService: IVppaService;
  @IRfqService rfqService: IRfqService;
  @IProjectService projectService: IProjectService;
  @IAppConfigService appConfigService: IAppConfigService;
  @INegotiationService negotiationService: INegotiationService;
  @IStructuredChatService structuredChatService: IStructuredChatService;
  @IIndividualProjectPageService individualProjectPageService: IIndividualProjectPageService;
  @IThirdPartyService thirdPartyService: IThirdPartyService;
  @IEnumService enumService: IEnumService;
  @IUserService userService: IUserService;

  // Modal config variables
  @observable show: boolean = false;
  @observable title = Labels.TITLE;
  @observable width: string = '662px';

  // User's currently logged in Company information
  @observable currentCompany: ICompany;

  @observable filterByValue: 'status' | 'project' = 'status';
  @observable negotiationStatuses = [];

  // Summary and Terms view variables
  @observable isSummaryOpened: boolean = false;
  @observable isNegotiationTermsOpened: boolean = false;

  @observable addRFQ: boolean = false;
  @observable linkMoreRFQs: boolean = false;

  // Offer Form Elements
  @observable fixedPrice: TextField;
  @observable offeredCapacity: TextField;
  @observable tenorStartDate: TextField;
  @observable tenorMonths: TextField;
  @observable buyerCODCollateral: TextField;
  @observable sellerCODCollateral: TextField;
  @observable fixedPriceValue: string = '';
  @observable offeredCapacityValue: string = '';
  @observable tenorStartDateValue: any;
  @observable tenorMonthsValue: string = '';
  @observable buyerCODCollateralValue: string = '';
  @observable sellerCODCollateralValue: string = '';
  @observable closeModalHeader: string = 'All unsent proposals will be discarded';
  @observable closeModalDescription: string = 'Are you sure you would like to proceed?';

  @observable displaySelector: string = 'none';
  @observable isMaskingEnabled: boolean;
  @observable companyUnmaskedProjects: Array<string> = [];

  @observable showLinkMore: boolean = false;
  @observable proposalValues: IProposal[] = [];
  @observable selectedNegotiation: INegotiation;

  // Back End Enum values
  @observable structuredChatEnums: StructuredChatEnums = {
    applicableRECProgram: [],
    applicableTrackingSystem: [],
    recDeliveryMethodology: [],
    technologyTypes: [],
    performanceGuaranteeMethod: [],
    negativePricingMethod: [],
    negotiationStatus: [],
    contractTypes: [],
  };

  private notifier1: any;
  private handler1: any;
  private negotiationDetailsHandler: any;
  private negotiationViewChanged: any;

  connectedCallback() {
    super.connectedCallback();
    this.loadEnums();
    this.showModalListener();
    this.checkBuyerPermission();
    this.getMaskingEnabled();
    this.thirdPartyService.loadArteriaUrl();
  }

  private showModalListener() {
    const _this = this;
    this.notifier1 = Observable.getNotifier(this.structuredChatService);
    this.handler1 = {
      handleChange(source: any, splices: Splice[]) {
        _this.currentCompany = _this.companyService.getCurrentCompany();
        _this.show = source.structuredChatVisibility;

        if (_this.show) {
          _this.rfqSelector.getRFQs();
          _this.checkDisplayRFQSelector();

          if (source.goToTradeConfirmationFlag) {
            _this.toggleSummary();
          }
        }
      },
    };
    this.negotiationDetailsHandler = {
      handleChange(source: any, splices: Splice[]) {
        _this.currentCompany = _this.companyService.getCurrentCompany();

        _this.loadNegotiationFields(source.negotiationInView);
        _this.loadProjectUnmasked(source.negotiationInView)

        if (source.allStagedNegotiations.length) {
          _this.loadNegotiationFields(source.allStagedNegotiations);
        }
      },
    };

    this.negotiationViewChanged = {
      handleChange(source: any, splices: Splice[]) {
        source.negotiationInViewChanged;
      },
    };

    this.notifier1.subscribe(this.handler1, 'structuredChatVisibility');
    this.notifier1.subscribe(this.negotiationViewChanged, 'negotiationInView');
    this.notifier1.subscribe(this.negotiationDetailsHandler, 'allProposals');
  }

  private async getMaskingEnabled() {
    await this.appConfigService.isMaskingFeatureEnabled().then((response) => {
      if (response.REPLY) {
        this.isMaskingEnabled = response.REPLY[0].IS_FEATURE_ENABLED
      }
    });
  }

  private checkBuyerPermission() {
    this.showLinkMore = !!this.userService
      .getFilteredPermissions()
      .includes(PROFILE_RIGHTS.BUYER_VISIBILITY);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.isSummaryOpened = false;
    this.isNegotiationTermsOpened = false;
    this.notifier1.unsubscribe(this.handler1);
    this.resetResponseForm();
    this.structuredChatService.closeStructuredChat();
  }

  // Redirect to the Individual Project page
  public openProjectPage() {
    this.individualProjectPageService.openIndividualProjectPage(
      this.structuredChatService.negotiationInViewDetails.PROJECT_ID,
    );
  }

  // Show summary view
  public toggleSummary() {
    this.isSummaryOpened = !this.isSummaryOpened;
  }

  // Show the negotiated terms
  public toggleNegotiationsTerms() {
    this.isNegotiationTermsOpened = !this.isNegotiationTermsOpened;
  }

  // Close modal function
  public closeModal() {
    this.isSummaryOpened = false;
    this.isNegotiationTermsOpened = false;
    this.linkMoreRFQs = false;
    if (this.addRFQ){
       this.addRfqForm.rfqForm.clearForm();
       this.rfqSelector.rfqGrid.gridApi.setFilterModel(null);
       this.rfqSelector.rfqGrid.gridApi.onFilterChanged();
    }
    this.addRFQ = false;
    this.resetModalValues();
    this.structuredChatService.closeStructuredChat();
  }

  // Clear all values and memory for structured chat
  public resetModalValues() {
    this.resetResponseForm();
    this.structuredChatService.disabledAcceptReject = true;
    this.structuredChatService.disabledCounter = false;
  }

  // Counter or Send offer function
  public sendOffer() {
    this.structuredChatService.disabledCounter = true;
    if (
      this.structuredChatService.negotiationInView.NEGOTIATION_STATUS !==
      VALID_STRUCTURED_CHAT_STATUS.STAGED
    ) {
      this.insertProposal(this.structuredChatService.selectedNegotiationID);
    } else {
      this.insertNegotiation().then((negotiationID) => {
        if (negotiationID) {
          this.insertProposal(negotiationID);
        }
      });
    }
  }

  public sendConfirm() {
    if (this.structuredChatService.selectedNegotiationID) {
      this.negotiationService
        .eventNegotiationConfirm(this.structuredChatService.selectedNegotiationID)
        .then(
          (response) => {
            if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
              this.resetResponseForm();
              this.toggleSummary();
            } else {
              notifyError(response.ERROR[0].TEXT);
              logger.error(response.ERROR);
            }
          },
          (error) => {
            notifyError(error);
            logger.error(error);
          },
        );
    }
  }

  public sendReject() {
    if (this.structuredChatService.selectedNegotiationID) {
      this.negotiationService
        .eventNegotiationReject(this.structuredChatService.selectedNegotiationID)
        .then(
          (response) => {
            if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
              this.resetResponseForm();
            } else {
              notifyError(response.ERROR[0].TEXT);
              logger.error(response);
            }
          },
          (error) => {
            notifyError(error);
            logger.error(error);
          },
        );
    }
  }

  public acceptNegotiation() {
    if (this.structuredChatService.selectedNegotiationID) {
      this.negotiationService
        .eventNegotiationAccept(this.structuredChatService.selectedNegotiationID)
        .then(
          (response) => {
            if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
              this.resetResponseForm();
              this.toggleSummary();
            } else {
              notifyError(response.ERROR[0].TEXT);
              logger.error(response);
            }
          },
          (error) => {
            notifyError(error);
            logger.error(error);
          },
        );
    }
  }

  public insertNegotiation() {
    return this.negotiationService
      .eventNegotiationInsert({
        VPPA_ID: this.structuredChatService.negotiationInView.VPPA_ID,
        RFQ_ID: this.structuredChatService.negotiationInView.RFQ_ID,
        BUYER_COMPANY_ID: '',
        SELLER_COMPANY_ID: '',
        PROJECT_ID: '',
      })
      .then(
        (response) => {
          if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
            return response.GENERATED[0]?.NEGOTIATION_ID;
          } else {
            notifyError(response.ERROR[0].TEXT);
            logger.error(response);
            return null;
          }
        },
        (error) => {
          notifyError(error);
          logger.error(error);
          return null;
        },
      );
  }

  public insertProposal(negotiationID) {
    const offerFormValues = this.getOfferValues();
    const offer: IProposalInsert = {
      ...{ NEGOTIATION_ID: negotiationID },
      ...offerFormValues,
    };

    this.structuredChatService.removeStagedNegotiation(
      this.structuredChatService.negotiationInView.NEGOTIATION_ID,
      negotiationID,
    );

    this.negotiationService.eventProposalInsert(offer).then(
      (response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
          // Doesn't have notification to not pollute the notifications
          this.resetResponseForm();
        } else {
          notifyError(response.ERROR[0].TEXT);
          logger.error(response);
        }
      },
      (error) => {
        notifyError(error);
        logger.error(error);
      },
    );
  }

  // Collect the values from the response form (bottom right section).
  // If value not provided send the previous value of the negotiation --value will always be provided
  public getOfferValues() {
    return {
      FIXED_PRICE: this.fixedPrice.value
        ? +this.fixedPrice.value
        : this.structuredChatService.negotiationInView.FIXED_PRICE,
      OFFERED_CAPACITY: this.offeredCapacity.value
        ? +this.offeredCapacity.value
        : this.structuredChatService.negotiationInView.OFFERED_CAPACITY,
      TENOR_MONTHS: this.tenorMonths.value
        ? +this.tenorMonths.value
        : this.structuredChatService.negotiationInView.TENOR_MONTHS,
      TENOR_START_DATE: this.tenorStartDate.value
        ? this.tenorStartDate.value
        : this.structuredChatService.negotiationInView.TENOR_START_DATE,
      BUYER_POST_COD_COLLATERAL: this.buyerCODCollateral.value
        ? +this.buyerCODCollateral.value
        : this.structuredChatService.negotiationInView.BUYER_POST_COD_COLLATERAL,
      SELLER_POST_COD_COLLATERAL: this.sellerCODCollateral.value
        ? +this.sellerCODCollateral.value
        : this.structuredChatService.negotiationInView.SELLER_POST_COD_COLLATERAL,
    };
  }

  // Reset response form inputs (bottom right section)
  public resetResponseForm() {
    if (
      this.fixedPrice &&
      this.offeredCapacity &&
      this.tenorStartDate &&
      this.tenorMonths &&
      this.buyerCODCollateral &&
      this.sellerCODCollateral
    ) {
      this.fixedPrice.value = '';
      this.offeredCapacity.value = '';
      this.tenorStartDate.value = '';
      this.tenorMonths.value = '';
      this.buyerCODCollateral.value = '';
      this.sellerCODCollateral.value = '';
    }
  }

  // Load all needed Enums from the BE
  private async loadEnums() {
    this.enumService.getEnumValues(CASCADE_ENUM.APPLICABLE_REC_PROGRAM).then((response) => {
      if (response.REPLY) {
        this.structuredChatEnums.applicableRECProgram = response.REPLY;
      }
    });


    this.enumService.getEnumValues(CASCADE_ENUM.TECHNOLOGY).then((response) => {
      if (response.REPLY) {
        this.structuredChatEnums.technologyTypes = response.REPLY;
      }
    });

    this.enumService.getEnumValues(CASCADE_ENUM.RFQ_NEGATIVE_PRICING_METHOD).then((response) => {
      if (response.REPLY) {
        this.structuredChatEnums.negativePricingMethod = response.REPLY;
      }
    });


    this.enumService.getEnumValues(CASCADE_ENUM.APPLICABLE_TRACKING_SYSTEM).then((response) => {
      if (response.REPLY) {
        this.structuredChatEnums.applicableTrackingSystem = response.REPLY;
      }
    });

    this.enumService.getEnumValues(CASCADE_ENUM.RFQ_REC_DELIVERY_METHODOLOGY).then((response) => {
      if (response.REPLY) {
        this.structuredChatEnums.recDeliveryMethodology = response.REPLY;
      }
    });


    this.enumService
      .getEnumValues(CASCADE_ENUM.RFQ_PERFORMANCE_GUARANTEE_METHOD)
      .then((response) => {
        if (response.REPLY) {
          this.structuredChatEnums.performanceGuaranteeMethod = response.REPLY;
        }
      });
    this.enumService.getFilteredEnumValues(CASCADE_ENUM.CONTRACT_TYPE).then((response) => {
      if (response.REPLY) {
        this.structuredChatEnums.contractTypes = response.REPLY;
      }
    });

    this.enumService.getEnumValues(CASCADE_ENUM.NEGOTIATION_STATUS).then((response) => {
      if (response.REPLY) {
        this.structuredChatEnums.negotiationStatus = response.REPLY;
        this.negotiationStatuses = this.structuredChatEnums.negotiationStatus;
        this.negotiationStatuses.unshift({
          ENUM_VALUE: VALID_STRUCTURED_CHAT_STATUS.STAGED,
          READABLE_VALUE: 'Staged',
        });
      }
    });
  }

  public async loadProjectUnmasked(negotiation) {
    if (negotiation) {
      this.projectService.listUnmaskedProjectsForBuyerCompany(negotiation.SELLER_COMPANY_ID, negotiation.BUYER_COMPANY_ID).then((response) => {
        if (
          response.REPLY &&
          negotiation.SELLER_COMPANY_ID === this.currentCompany.CASCADE_COMPANY_ID
        ) {
          this.companyUnmaskedProjects = response.REPLY[0].PROJECT_IDS
         }
    });
    }
  }

  public async loadNegotiationFields(negotiation) {
    if (
      negotiation &&
      negotiation.NEGOTIATION_STATUS === VALID_STRUCTURED_CHAT_STATUS.IN_PROGRESS
    ) {
      if (this.structuredChatService.allProposals.length > 0) {
        const proposals = this.structuredChatService.allProposals;
        setTimeout(() => {
          this.structuredChatService.disabledCounter = this.structuredChatService
            .disabledAcceptReject
            ? true
            : false;
        }, 100);

        if (
          this.structuredChatService.isCompanyProposal(proposals[0]) &&
          proposals[0].PROPOSAL_SENDER === 'SELLER'
        ) {
          //current user: seller, last proposal from seller
          this.resetProposalForm();
          this.fixedPriceValue = String(proposals[0].FIXED_PRICE);
          this.offeredCapacityValue = String(proposals[0].OFFERED_CAPACITY);
          this.tenorStartDateValue = formatDateForInput(Number(proposals[0].TENOR_START_DATE));
          this.tenorMonthsValue = String(proposals[0].TENOR_MONTHS);
          this.buyerCODCollateralValue = negotiation.BUYER_POST_COD_COLLATERAL
            ? negotiation.BUYER_POST_COD_COLLATERAL
            : getAbbreviationForCreditProfile(negotiation.RFQ_CREDIT_PROFILE);
          this.sellerCODCollateralValue = proposals[0].SELLER_POST_COD_COLLATERAL
            ? String(proposals[0].SELLER_POST_COD_COLLATERAL)
            : getAbbreviationForCreditProfile(proposals[0].VPPA_CREDIT_PROFILE);
        }

        if (
          this.structuredChatService.isCompanyProposal(proposals[0]) &&
          proposals[0].PROPOSAL_SENDER === 'BUYER'
        ) {
          //current user: buyer, last proposal from buyer
          this.resetProposalForm();
          this.fixedPriceValue = String(proposals[0].FIXED_PRICE);
          this.offeredCapacityValue = String(proposals[0].OFFERED_CAPACITY);
          this.tenorStartDateValue = formatDateForInput(Number(proposals[0].TENOR_START_DATE));
          this.tenorMonthsValue = String(proposals[0].TENOR_MONTHS);
          this.buyerCODCollateralValue = proposals[0].BUYER_POST_COD_COLLATERAL
            ? String(proposals[0].BUYER_POST_COD_COLLATERAL)
            : getAbbreviationForCreditProfile(proposals[0].RFQ_CREDIT_PROFILE);
          this.sellerCODCollateralValue = negotiation.SELLER_POST_COD_COLLATERAL
            ? negotiation.SELLER_POST_COD_COLLATERAL
            : getAbbreviationForCreditProfile(negotiation.VPPA_CREDIT_PROFILE);
        }
        if (this.structuredChatService.allProposals.length > 1) {
          if (
            !this.structuredChatService.isCompanyProposal(proposals[0]) &&
            proposals[0].PROPOSAL_SENDER === 'BUYER'
          ) {
            //current user: seller, second to last proposal from seller
            this.resetProposalForm();
            this.fixedPriceValue = String(proposals[1].FIXED_PRICE);
            this.offeredCapacityValue = String(proposals[1].OFFERED_CAPACITY);
            this.tenorStartDateValue = formatDateForInput(Number(proposals[1].TENOR_START_DATE));
            this.tenorMonthsValue = String(proposals[1].TENOR_MONTHS);
            this.buyerCODCollateralValue = negotiation.BUYER_POST_COD_COLLATERAL
              ? negotiation.BUYER_POST_COD_COLLATERAL
              : getAbbreviationForCreditProfile(negotiation.RFQ_CREDIT_PROFILE);
            this.sellerCODCollateralValue = proposals[1].SELLER_POST_COD_COLLATERAL
              ? String(proposals[1].SELLER_POST_COD_COLLATERAL)
              : getAbbreviationForCreditProfile(proposals[1].VPPA_CREDIT_PROFILE);
          }

          if (
            !this.structuredChatService.isCompanyProposal(proposals[0]) &&
            proposals[0].PROPOSAL_SENDER === 'SELLER'
          ) {
            //current user: buyer, second to last proposal from buyer
            this.resetProposalForm();
            this.fixedPriceValue = String(proposals[1].FIXED_PRICE);
            this.offeredCapacityValue = String(proposals[1].OFFERED_CAPACITY);
            this.tenorStartDateValue = formatDateForInput(Number(proposals[1].TENOR_START_DATE));
            this.tenorMonthsValue = String(proposals[1].TENOR_MONTHS);
            this.buyerCODCollateralValue = proposals[1].BUYER_POST_COD_COLLATERAL
              ? String(proposals[1].BUYER_POST_COD_COLLATERAL)
              : getAbbreviationForCreditProfile(proposals[1].RFQ_CREDIT_PROFILE);
            this.sellerCODCollateralValue = negotiation.SELLER_POST_COD_COLLATERAL
              ? negotiation.SELLER_POST_COD_COLLATERAL
              : getAbbreviationForCreditProfile(negotiation.VPPA_CREDIT_PROFILE);
          }
        } else {
          if (
            !this.structuredChatService.isCompanyProposal(proposals[0]) &&
            proposals[0].PROPOSAL_SENDER === 'BUYER'
          ) {
            //current user: seller, one proposal from buyer
            this.resetProposalForm();
            const vppa = await this.getVppaData(negotiation.VPPA_ID);
            this.fixedPriceValue = String(vppa?.FIXED_PRICE);
            this.offeredCapacityValue = String(vppa?.OFFERED_CAPACITY);
            this.tenorStartDateValue = formatDateForInput(Number(vppa?.TENOR_START_DATE));
            this.tenorMonthsValue = String(vppa?.TENOR_MONTHS);
            this.buyerCODCollateralValue = negotiation.BUYER_POST_COD_COLLATERAL
              ? negotiation.BUYER_POST_COD_COLLATERAL
              : getAbbreviationForCreditProfile(negotiation.RFQ_CREDIT_PROFILE);
            this.sellerCODCollateralValue = vppa?.SELLER_POST_COD_COLLATERAL
              ? String(vppa?.SELLER_POST_COD_COLLATERAL)
              : getAbbreviationForCreditProfile(vppa?.VPPA_CREDIT_PROFILE);
          }

          if (
            !this.structuredChatService.isCompanyProposal(proposals[0]) &&
            proposals[0].PROPOSAL_SENDER === 'SELLER'
          ) {
            //current user: buyer, one proposal from seller
            this.resetProposalForm();
            const rfq = await this.getRFQData(negotiation.RFQ_ID);
            if (rfq.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
              this.fixedPriceValue = String(rfq.FIXED_PRICE);
            } else {
              this.fixedPriceValue = String(
                (rfq.FIXED_PRICE_LOWER_BOUND + rfq.FIXED_PRICE_UPPER_BOUND) / 2,
              );
            }
            this.offeredCapacityValue = getFormattedNumber(
              Number(await this.getRfqVolumeinMW(rfq.REQUESTED_VOLUME, negotiation.VPPA_ID)),
            );
            this.tenorStartDateValue = formatDateForInput(Number(rfq.TENOR_START_DATE));
            this.tenorMonthsValue = String(rfq.TENOR_MONTHS);
            this.buyerCODCollateralValue = rfq.BUYER_POST_COD_COLLATERAL
              ? String(rfq.BUYER_POST_COD_COLLATERAL)
              : getAbbreviationForCreditProfile(rfq.RFQ_CREDIT_PROFILE);
            this.sellerCODCollateralValue = negotiation.SELLER_POST_COD_COLLATERAL
              ? negotiation.SELLER_POST_COD_COLLATERAL
              : getAbbreviationForCreditProfile(negotiation.VPPA_CREDIT_PROFILE);
          }
        }
      }
    } else if (
      negotiation &&
      negotiation.NEGOTIATION_STATUS === VALID_STRUCTURED_CHAT_STATUS.STAGED
    ) {
      this.resetProposalForm();
      if (this.isCurrentUserBuyer()) {
        //current user: buyer, staged negotiation from buyer
        const rfq = await this.getRFQData(negotiation.RFQ_ID);
        if (rfq.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
          this.fixedPriceValue = String(rfq.FIXED_PRICE);
          this.offeredCapacityValue = String(rfq.CAPACITY);
        } else {
          this.fixedPriceValue = String(
            (rfq.FIXED_PRICE_LOWER_BOUND + rfq.FIXED_PRICE_UPPER_BOUND) / 2,
          );
          this.offeredCapacityValue = String(rfq.CAPACITY);
        }
        this.offeredCapacityValue = getFormattedNumber(
          Number(await this.getRfqVolumeinMW(rfq.REQUESTED_VOLUME, negotiation.VPPA_ID)),
        );
        this.tenorStartDateValue = formatDateForInput(Number(rfq.TENOR_START_DATE));
        this.tenorMonthsValue = String(rfq.TENOR_MONTHS);
        this.buyerCODCollateralValue = rfq.BUYER_POST_COD_COLLATERAL
          ? String(rfq.BUYER_POST_COD_COLLATERAL)
          : getAbbreviationForCreditProfile(rfq.RFQ_CREDIT_PROFILE);
        this.sellerCODCollateralValue = negotiation.SELLER_POST_COD_COLLATERAL
          ? getAbbreviationForCreditProfile(negotiation.VPPA_CREDIT_PROFILE)
          : negotiation.SELLER_POST_COD_COLLATERAL;
      } else if (!this.isCurrentUserBuyer()) {
        //current user: seller, staged negotiation from seller
        this.fixedPriceValue = negotiation.FIXED_PRICE;
        this.offeredCapacityValue = negotiation.OFFERED_CAPACITY;
        this.tenorStartDateValue = formatDateForInput(negotiation.TENOR_START_DATE);
        this.tenorMonthsValue = negotiation.TENOR_MONTHS;
        this.buyerCODCollateralValue = negotiation.BUYER_POST_COD_COLLATERAL
          ? negotiation.BUYER_POST_COD_COLLATERAL
          : getAbbreviationForCreditProfile(negotiation.RFQ_CREDIT_PROFILE);
        this.sellerCODCollateralValue = negotiation.SELLER_POST_COD_COLLATERAL
          ? negotiation.SELLER_POST_COD_COLLATERAL
          : getAbbreviationForCreditProfile(negotiation.VPPA_CREDIT_PROFILE);
      }
      setTimeout(() => {
        this.structuredChatService.disabledCounter = false;
      }, 200);
    }
  }

  private resetProposalForm() {
    this.fixedPriceValue = null;
    this.offeredCapacityValue = null;
    this.tenorStartDateValue = '';
    this.tenorMonthsValue = null;
    this.buyerCODCollateralValue = '';
    this.sellerCODCollateralValue = '';
  }

  private getVppaData(vppaID?: string) {
    return this.vppaService.getVppa(vppaID).then((response) => {
      if (response) {
        return response.REPLY[0] as IVppa;
      }

      return null;
    });
  }

  private getRFQData(rfqID?: string) {
    return this.rfqService.getRfq(rfqID).then((response) => {
      if (response) {
        return response.REPLY[0] as IRfq;
      }

      return null;
    });
  }

  private async getRfqVolumeinMW(rfqVolume, vppaId) {
    let rfqOfferedCapacity = '';
    await this.vppaService.getProjectKeyMetrics(vppaId).then((response) => {
      const hoursOnYear = 8760;
      if (response && response.REPLY.length > 0 && response.REPLY[0].IMPLIED_CAPACITY_FACTOR) {
        const impliedCapacityFactor = Number(response.REPLY[0].IMPLIED_CAPACITY_FACTOR.toFixed(2));
        rfqOfferedCapacity = (rfqVolume / (hoursOnYear * impliedCapacityFactor)).toString();
      }
    });
    return rfqOfferedCapacity;
  }

  // Checks if there are any changes in the values of the most recent proposal and the previous proposal
  public counterOfferChanges(proposalID: string): OfferValueChanged {
    const offerValueChangedBooleanBoolean: OfferValueChanged = {
      fixedPriceHasChanged: false,
      offeredCapacityHasChanged: false,
      tenorStartDateHasChanged: false,
      tenorMonthsHasChanged: false,
      buyerCODCollateralHasChanged: false,
      sellerCODCollateralHasChanged: false,
    };
    if (
      this.structuredChatService.allProposals &&
      this.structuredChatService.negotiationInViewDetails
    ) {
      const currentProposal = this.structuredChatService.allProposals.find((obj) => {
        return obj.PROPOSAL_ID === proposalID;
      });

      const originalOffers = this.structuredChatService.negotiationInViewDetails;
      const currentProposalIndex = this.structuredChatService.allProposals.indexOf(currentProposal);
      if (currentProposalIndex !== this.structuredChatService.allProposals.length - 1) {
        const previousProposal = this.structuredChatService.allProposals.at(
          currentProposalIndex + 1,
        );
        offerValueChangedBooleanBoolean.fixedPriceHasChanged =
          previousProposal.FIXED_PRICE !== currentProposal.FIXED_PRICE;
        offerValueChangedBooleanBoolean.offeredCapacityHasChanged =
          previousProposal.OFFERED_CAPACITY !== currentProposal.OFFERED_CAPACITY;
        offerValueChangedBooleanBoolean.tenorStartDateHasChanged =
          previousProposal.TENOR_START_DATE !== currentProposal.TENOR_START_DATE;
        offerValueChangedBooleanBoolean.tenorMonthsHasChanged =
          previousProposal.TENOR_MONTHS !== currentProposal.TENOR_MONTHS;
        offerValueChangedBooleanBoolean.buyerCODCollateralHasChanged =
          previousProposal.BUYER_POST_COD_COLLATERAL !== currentProposal.BUYER_POST_COD_COLLATERAL;
        offerValueChangedBooleanBoolean.sellerCODCollateralHasChanged =
          previousProposal.SELLER_POST_COD_COLLATERAL !==
          currentProposal.SELLER_POST_COD_COLLATERAL;
      } else {
        // for the first counter offer that is made
        if (currentProposal.PROPOSAL_SENDER === 'BUYER') {
          offerValueChangedBooleanBoolean.fixedPriceHasChanged =
            originalOffers.VPPA_FIXED_PRICE !== currentProposal.FIXED_PRICE;
          offerValueChangedBooleanBoolean.offeredCapacityHasChanged =
            originalOffers.VPPA_OFFERED_CAPACITY !== currentProposal.OFFERED_CAPACITY;
          offerValueChangedBooleanBoolean.tenorStartDateHasChanged =
            originalOffers.VPPA_TENOR_START_DATE !== currentProposal.TENOR_START_DATE;
          offerValueChangedBooleanBoolean.tenorMonthsHasChanged =
            originalOffers.VPPA_TENOR_MONTHS !== currentProposal.TENOR_MONTHS;
          offerValueChangedBooleanBoolean.buyerCODCollateralHasChanged =
            originalOffers.RFQ_COLLATERAL !== currentProposal.BUYER_POST_COD_COLLATERAL;
          offerValueChangedBooleanBoolean.sellerCODCollateralHasChanged =
            originalOffers.VPPA_COLLATERAL !== currentProposal.SELLER_POST_COD_COLLATERAL;
        } else if (currentProposal.PROPOSAL_SENDER === 'SELLER') {
          offerValueChangedBooleanBoolean.fixedPriceHasChanged =
            parseFloat(originalOffers.RFQ_FIXED_PRICE) !== currentProposal.FIXED_PRICE;
          offerValueChangedBooleanBoolean.offeredCapacityHasChanged =
            originalOffers.RFQ_OFFERED_CAPACITY !== currentProposal.OFFERED_CAPACITY.toString();
          offerValueChangedBooleanBoolean.tenorStartDateHasChanged =
            originalOffers.RFQ_TENOR_START_DATE !== currentProposal.TENOR_START_DATE;
          offerValueChangedBooleanBoolean.tenorMonthsHasChanged =
            originalOffers.RFQ_TENOR_MONTHS !== currentProposal.TENOR_MONTHS;
          offerValueChangedBooleanBoolean.buyerCODCollateralHasChanged =
            originalOffers.RFQ_COLLATERAL !== currentProposal.BUYER_POST_COD_COLLATERAL;
          offerValueChangedBooleanBoolean.sellerCODCollateralHasChanged =
            originalOffers.VPPA_COLLATERAL !== currentProposal.SELLER_POST_COD_COLLATERAL;
        }
      }
    }

    return offerValueChangedBooleanBoolean;
  }

  public showRFQSelector() {
    this.linkMoreRFQs = true;
    this.rfqSelector.getRFQs();
    this.checkDisplayRFQSelector();
  }

  public showAddRFQ(addRFQ: boolean) {
    const selectedRFQs = this.rfqSelector.rfqGrid.gridApi.getSelectedRows();
    this.structuredChatService.selectedRFQIDs = selectedRFQs
      .filter((rfq) => {
        if (!rfq.IS_LINKED) return rfq;
      })
      .map((rfq) => rfq.RFQ.RFQ_ID);

    this.addRFQ = addRFQ;
  }

  public closeAddRFQ(closeRFQ: boolean) {
    this.showRFQSelector();
    this.addRFQ = !closeRFQ;
  }

  public checkDisplayRFQSelector() {
    this.displaySelector =
      this.linkMoreRFQs ||
      (this.structuredChatService.selectedVPPAIDs.length &&
        !this.structuredChatService.selectedRFQIDs.length)
        ? ''
        : 'none';
  }

  public closeRFQSelector(c) {
    this.linkMoreRFQs = !eventDetail(c);
    this.checkDisplayRFQSelector();
  }

  public isCurrentUserBuyer() {
    return this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.BUYER_VISIBILITY);
  }

  public returnToNegotiation() {
    this.negotiationService
      .eventReturnToNegotiation(this.structuredChatService.negotiationInView.NEGOTIATION_ID)
      .then(
        (response) => {
          if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
            this.toggleSummary();
          } else {
            notifyError(response.ERROR[0].TEXT);
            logger.error(response);
          }
        },
        (error) => {
          notifyError(error);
          logger.error(error);
        },
      );
  }

  public goToArteriaNegotiationsPage() {
    this.thirdPartyService.openArteriaNegotiations();
  }
}

export interface StructuredChatEnums {
  applicableRECProgram: Array<EnumTranslate>;
  applicableTrackingSystem: Array<EnumTranslate>;
  recDeliveryMethodology: Array<EnumTranslate>;
  technologyTypes: Array<EnumTranslate>;
  performanceGuaranteeMethod: Array<EnumTranslate>;
  negativePricingMethod: Array<EnumTranslate>;
  negotiationStatus: Array<EnumTranslate>;
  contractTypes: Array<EnumTranslate>;
}
